import { useCallback, useState, useEffect, useRef } from "react";
import { ChatConversation, ChatMessage } from "../types";
import { useAppState } from "../../../context/StateContext";
import { Convo, getConvos } from "Api/dumontApi";
import { useDumont } from "Context/DumontContext";


const TEMP_NEW_CONVERSATION_ID = "new";

/**
 * Converts Dumont Convo (backend) to ChatConversation (frontend)
 */
function convoToChatConversation(convo: Convo): ChatConversation {
  const id = convo.conversationId ?? "";
  return {
    id,
    title: (convo.title as string) || "New Conversation",
    createdAt: convo.createdAt ? new Date(convo.createdAt) : new Date(),
    updatedAt: convo.updatedAt ? new Date(convo.updatedAt) : new Date(),
    messages: [],
  };
}


export const useConversations = () => {
  const { baseUrl, token } = useDumont();
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetched = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchConversations = useCallback(async (): Promise<ChatConversation[]> => {
    if (!baseUrl || !token) return [];

    if (isMountedRef.current) setIsLoading(true);
    try {
      const res = await getConvos(baseUrl, token, { limit: 100 });
      const list = res.conversations ?? [];
      const converted = list.map(convoToChatConversation).filter((c) => c.id);
      if (isMountedRef.current) setConversations(converted);
      return converted;
    } catch (error) {
      console.error("[Dumont] Failed to fetch conversations:", error);
      return [];
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [baseUrl, token]);

  useEffect(() => {
    if (baseUrl && token && !hasFetched.current) {
      hasFetched.current = true;
      fetchConversations();
    }
  }, [baseUrl, token, fetchConversations]);

  const getConversations = useCallback((): ChatConversation[] => {
    return conversations;
  }, [conversations]);

  /**
  * Dumont has no separate create-conversation endpoint. We add a local conversation
  * with temp id "new"; the real id comes from postChat on first message.
  */
  const saveConversation = useCallback(
    async (conversation: ChatConversation): Promise<string | null> => {
      if (!baseUrl || !token) return null;
      if (!isMountedRef.current) return null;

      const newConversation: ChatConversation = {
        ...conversation,
        id: TEMP_NEW_CONVERSATION_ID,
        updatedAt: new Date(),
      };
      if (isMountedRef.current) setConversations((prev) => [newConversation, ...prev]);
      return TEMP_NEW_CONVERSATION_ID;
    },
    [baseUrl, token, setConversations]
  );

  const updateConversation = useCallback(
    async (conversationId: string, messages: ChatMessage[]): Promise<boolean> => {
      if (!baseUrl || !token) return false;
      if (!isMountedRef.current) return false;

      setConversations((prev) =>
        isMountedRef.current
          ? prev.map((c) =>
              c.id === conversationId ? { ...c, messages, updatedAt: new Date() } : c
            )
          : prev
      );

      return true;
    },
    [baseUrl, token]
  );

  const updateConversationTitle = useCallback((conversationId: string, title: string) => {
    if (!conversationId || !title || !baseUrl || !token) return;
    if (!isMountedRef.current) return;

    setConversations((prev) =>
      isMountedRef.current ? prev.map((c) => (c.id === conversationId ? { ...c, title } : c)) : prev
    );
  }, [baseUrl, token, setConversations]);

  /**
   * Dumont API has no delete-conversation endpoint; remove from local state only.
   */
  const deleteConversation = useCallback(async (conversationId: string): Promise<boolean> => {
    if (!conversationId || !baseUrl || !token) return false;
    if (!isMountedRef.current) return false;

    setConversations((prev) =>
      isMountedRef.current ? prev.filter((c) => c.id !== conversationId) : prev
    );
    return true;
  }, [baseUrl, token, setConversations]);

  const replaceConversationId = useCallback((oldId: string, newId: string) => {
    if (!oldId || !newId || !baseUrl || !token) return;
    if (!isMountedRef.current) return;

    setConversations((prev) =>
      isMountedRef.current ? prev.map((c) => (c.id === oldId ? { ...c, id: newId } : c)) : prev
    );
  }, [baseUrl, token, setConversations]);

  const filterConversations = useCallback(
    (searchQuery: string): ChatConversation[] => {
      if (!searchQuery.trim()) return conversations;
      const q = searchQuery.toLowerCase();
      return conversations.filter(
        (conv) =>
          conv.title.toLowerCase().includes(q) ||
          conv.messages.some((m) => m.text.toLowerCase().includes(q))
      );
    },
    [conversations]
  );

  const formatRelativeDate = useCallback((date: Date): string => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return `Today, ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
    } else if (diffInDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
    } else if (diffInDays < 7) {
      return date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
    } else {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
  }, []);

  return {
    getConversations,
    saveConversation,
    updateConversation,
    updateConversationTitle,
    deleteConversation,
    replaceConversationId,
    filterConversations,
    formatRelativeDate,
    fetchConversations,
    isLoading,
  } as const;
};

